import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Link, useLocation, useNavigate } from "react-router-dom";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemButton from "@mui/material/ListItemButton";
import RefreshIcon from "@mui/icons-material/Refresh";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { useForm, Controller } from "react-hook-form";

const InspirePromptModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      promptRef,
      jobconf = {},
      setJobconf,
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [loadingPrompt, setLoadingPrompt] = useState(false);
    const [inspiredPrompts, setInspiredPrompts] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
      if (promptRef?.current?.value || (jobconf?.prompt_array && jobconf.prompt_array.length > 0)) {
        const text = promptRef?.current?.value;
        handleInspirePrompt(text);
      }
    }, []);

    const handleSave = () => {
      if (promptRef?.current && selectedIndex != null) {
        promptRef.current.value = inspiredPrompts[selectedIndex] || "";
      }

      handleClose();
    };

    const handleInspirePrompt = async (text) => {
      let input_text = text || "";
      if (jobconf?.prompt_array && jobconf.prompt_array.length > 0) {
        if (input_text) {
          input_text = input_text + ". " + jobconf.prompt_array.join(",");
        } else {
          input_text = jobconf.prompt_array.join(",");
        }
      }

      if (!input_text) {
        return toast.error("Please provide some hint!");
      }
      try {
        const payload = {
          data: {
            input_text: input_text,
          },
        };
        setLoadingPrompt(true);
        const res = await api.ailab.promptRec(payload);
        let prompt_rec = res?.data?.result?.results?.answer || "";

        prompt_rec = prompt_rec.replace(/Design \d+:\s*\n*/g, "");

        const arr = prompt_rec.split(/\n+/);

        setInspiredPrompts(arr);
        setLoadingPrompt(false);
      } catch (err) {
        console.log(err);
        setLoadingPrompt(false);
        handleClose();
        toast.error("Failed to get inspired Prompts!");
      }
    };

    return (
      <SuiBox>
        <Dialog
          open={visible}
          onClose={(e, reason) => {
            if (reason != "backdropClick") {
              handleClose();
            }
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="md"
          fullWidth={true}
        >
          <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={loadingPrompt}>
            <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
            <SuiTypography variant="h6" fontWeight="medium" color="info">
              Loading...
            </SuiTypography>
          </Backdrop>

          <DialogTitle id="alert-dialog-title">
            Prompt Inspiration
            <IconButton
              aria-label="close"
              onClick={() => handleClose()}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {!loadingPrompt ? (
              <div>
                <SuiTypography variant="body1" color="dark" sx={{ ml: 1 }}>
                  Select the desired prompt and click <strong>Apply</strong> to update it.
                </SuiTypography>

                <List sx={{ width: "100%", bgcolor: "background.paper" }}>
                  {inspiredPrompts.map((text, index) => (
                    <ListItemButton
                      selected={selectedIndex === index}
                      onClick={(event) => {
                        setSelectedIndex(index);
                      }}
                    >
                      <ListItemIcon>
                        <Tooltip title="Replace with current prompt.">
                          <IconButton
                            aria-label="replace"
                            color={selectedIndex === index ? "success" : "secondary"}
                          >
                            <PublishedWithChangesIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItemButton>
                  ))}
                </List>
              </div>
            ) : null}
          </DialogContent>
          <DialogActions>
            <SuiButton
              variant="gradient"
              size="medium"
              color="info"
              onClick={() => {
                const text = promptRef?.current?.value;
                handleInspirePrompt(text);
                setSelectedIndex(null);
              }}
            >
              Refresh
            </SuiButton>
            <SuiButton
              size="medium"
              disabled={selectedIndex == null}
              variant="gradient"
              color="dark"
              onClick={handleSave}
            >
              Apply
            </SuiButton>
          </DialogActions>
        </Dialog>
      </SuiBox>
    );
  }
);

export default InspirePromptModal;
